import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { ReactNode, useContext, useEffect, useState } from 'react';

import { useCapability } from '@vimeo/core/hooks/use-capability/useCapability';
import { ViewerContext } from '@vimeo/viewer';

import { FixableContainer } from './FixableContainer';

const ReviewEditAccessBanner = dynamic(() => import('./ReviewEditAccessBanner'), { ssr: false });

const GlobalNotificationsBanner = dynamic(
  () => import('@vimeo-notifications/global/src/Container'),
  { ssr: false },
);

const FBCreativeContextBanner = dynamic(() => import('./FBCreativeContextBanner'), {
  ssr: false,
});

const CreatePageTeamMemberPrivilegesBanner = dynamic(
  () => import('./CreatePageTeamMemberPrivilegesBanner'),
  { ssr: false },
);

const StorageQuotaWarningBanner = dynamic(() => import('./StorageQuotaWarningBanner'), {
  ssr: false,
});

type Props = {
  globalNotifications?: boolean;
  enableReviewEditAccessBanner?: boolean;
  fixed?: boolean;
  children?: ReactNode;
};

export function GlobalBanners({
  globalNotifications = true,
  enableReviewEditAccessBanner,
  fixed,
}: Props) {
  const router = useRouter();
  const viewer = useContext(ViewerContext);
  const [showFbBanner, setShowFbBanner] = useState(false);

  const {
    capabilities: { hasSimplifiedEnterpriseAccount },
    loading,
  } = useCapability(['hasSimplifiedEnterpriseAccount'], viewer?.user?.uri);

  useEffect(() => {
    setShowFbBanner(
      typeof document !== 'undefined' && document.cookie.indexOf('create_fb_context=1') !== -1,
    );
  }, []);

  const pathToShowBanner = router.pathname.startsWith('/create/templates');
  const showBanner =
    pathToShowBanner && viewer?.teamUser && !loading && !hasSimplifiedEnterpriseAccount;

  return (
    <FixableContainer fixed={fixed} style={{ top: 0 }}>
      <StorageQuotaWarningBanner />
      {!router.pathname.startsWith('/create') &&
        !router.pathname.startsWith('/preview') &&
        showFbBanner && <FBCreativeContextBanner />}
      {/* This could be moved to this specific page */}
      {showBanner && (
        <CreatePageTeamMemberPrivilegesBanner
          teamUserRole={viewer?.teamUser?.plainTextPermissionLevel || ''}
          userId={viewer.user!.id}
          locale={viewer.locale}
          vuid={viewer.vuid}
        />
      )}
      {/* wrap_content is currently used to tell the mod_panel where to be injected in the page
          see: web/assets/js/apps/modworld/modules/mod_panel.js
        */}
      <div className="wrap_content" />
      {globalNotifications && viewer?.xsrft && <GlobalNotificationsBanner token={viewer.xsrft} />}
      {enableReviewEditAccessBanner && <ReviewEditAccessBanner />}
    </FixableContainer>
  );
}
